import i18n from "../i18n";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Group,
  MultiSelect,
  Paper,
  Popover,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconDeviceFloppy } from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import { useServerApi } from "../hooks/userServerApi";
import { withCustomDocuments } from "../data/entityEmailDocuments";
import {
  fetchEmailTemplatesForTypes,
  invalidateEmailTemplateListCache,
} from "../helpers/emailTemplateListLoader";

const BUILTIN = "__builtin__";

function EntityDocumentEmailFields({
  targetEntity,
  templateType,
  passwordTemplateType,
  subject,
  body,
  cc,
  bcc,
  attachmentKeys,
  encrypt,
  passwordSubject,
  passwordBody,
  onChange,
  readOnly = false,
  grouped = false,
  between = null,
}) {
  const [api] = useServerApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [templates, setTemplates] = useState([]);
  const [passwordTemplates, setPasswordTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(BUILTIN);
  const [passwordTemplateId, setPasswordTemplateId] = useState(BUILTIN);
  const [saveOpen, setSaveOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [saving, setSaving] = useState(false);
  const [customDocs, setCustomDocs] = useState([]);
  const documents = withCustomDocuments(targetEntity, customDocs);

  useEffect(() => {
    let cancelled = false;
    if (!targetEntity) {
      setCustomDocs([]);
      return undefined;
    }
    (async () => {
      try {
        const result = await apiRef.current.customDocument.list({
          entity: targetEntity,
          active: true,
          hasTemplate: true,
        });
        if (!cancelled) {
          setCustomDocs(result?.success ? result.data?.documents || [] : []);
        }
      } catch (error) {
        if (!cancelled) setCustomDocs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetEntity]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { templates: loaded, passwordTemplates: loadedPassword } =
          await fetchEmailTemplatesForTypes(
            apiRef.current,
            templateType,
            passwordTemplateType
          );
        if (cancelled) return;
        setTemplates(loaded);
        setPasswordTemplates(loadedPassword);
      } catch (error) {
        if (!cancelled) {
          setTemplates([]);
          setPasswordTemplates([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateType, passwordTemplateType]);

  const applyTemplate = (tpl) => {
    setTemplateId(tpl?._id || BUILTIN);
    if (!tpl) return;
    onChange({
      subject: tpl.subject || "",
      body: tpl.body || "",
      cc: tpl.cc || "",
      bcc: tpl.bcc || "",
    });
  };

  const saveTemplate = async () => {
    const name = templateName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const res = await api.add({
        apiEntity: "emailTemplate",
        values: {
          name,
          type: templateType,
          subject: subject || "",
          body: body || "",
          cc: cc || "",
          bcc: bcc || "",
          isDefault: false,
        },
      });
      if (!res?.success) {
        showNotification({
          color: "red",
          title: "Could not save template",
          message: res?.error?.msg || res?.message || "",
        });
        return;
      }
      const created = res.data;
      invalidateEmailTemplateListCache(templateType, passwordTemplateType);
      setTemplates((current) => {
        const next = current.filter(
          (doc) => doc._id !== created?._id && doc.name !== name
        );
        return created ? [...next, created] : next;
      });
      setTemplateId(created?._id || BUILTIN);
      setSaveOpen(false);
      setTemplateName("");
      showNotification({ color: "green", title: "Template saved" });
    } finally {
      setSaving(false);
    }
  };

  const templateFields = (
    <>
      <Select
        label="Email template"
        data={[
          { value: BUILTIN, label: "Current content" },
          ...templates.map((doc) => ({ value: doc._id, label: doc.name })),
        ]}
        value={templateId}
        disabled={readOnly}
        onChange={(id) => {
          if (!id || id === BUILTIN) {
            setTemplateId(BUILTIN);
            return;
          }
          applyTemplate(templates.find((doc) => doc._id === id) || null);
        }}
      />
    </>
  );

  const saveFields = !readOnly ? (
        <Group>
          <Popover
            opened={saveOpen}
            onChange={setSaveOpen}
            position="bottom-start"
            withArrow
          >
            <Popover.Target>
              <Button
                variant="default"
                leftIcon={<IconDeviceFloppy size={16} />}
                onClick={() => setSaveOpen((open) => !open)}
              >{i18n.t("Save as template")}</Button>
            </Popover.Target>
            <Popover.Dropdown>
              <Stack spacing="xs">
                <TextInput
                  label="Template name"
                  value={templateName}
                  onChange={(event) =>
                    setTemplateName(event.currentTarget.value)
                  }
                />
                <Button size="xs" loading={saving} onClick={saveTemplate}>{i18n.t("Save")}</Button>
              </Stack>
            </Popover.Dropdown>
          </Popover>
        </Group>
      ) : null;

  const deliveryFields = (
    <>
      <SimpleGrid cols={2} spacing="sm" breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
        <TextInput
          label="CC"
          placeholder="Optional"
          value={cc || ""}
          disabled={readOnly}
          onChange={(event) => onChange({ cc: event.currentTarget.value })}
        />
        <TextInput
          label="BCC"
          placeholder="Optional"
          value={bcc || ""}
          disabled={readOnly}
          onChange={(event) => onChange({ bcc: event.currentTarget.value })}
        />
      </SimpleGrid>
      {documents.length ? (
        <MultiSelect
          label="Attachments"
          description="Select one or more documents for this record"
          data={documents}
          value={attachmentKeys || []}
          disabled={readOnly}
          onChange={(keys) => onChange({ attachmentKeys: keys })}
          clearable
        />
      ) : (
        <Text size="xs" color="dimmed">
          This entity has no documents to attach.
        </Text>
      )}
      <Checkbox
        label="Password-protect the PDFs"
        description="A second email sends the password. It is not stored."
        checked={!!encrypt}
        disabled={readOnly || !documents.length}
        onChange={(event) =>
          onChange({ encrypt: event.currentTarget.checked })
        }
      />
      {encrypt ? (
        <Stack spacing="xs">
          <Select
            label="Password email template"
            data={[
              { value: BUILTIN, label: "Current password email" },
              ...passwordTemplates.map((doc) => ({
                value: doc._id,
                label: doc.name,
              })),
            ]}
            value={passwordTemplateId}
            disabled={readOnly}
            onChange={(id) => {
              setPasswordTemplateId(id || BUILTIN);
              if (!id || id === BUILTIN) return;
              const tpl = passwordTemplates.find((doc) => doc._id === id);
              if (!tpl) return;
              onChange({
                passwordSubject: tpl.subject || "",
                passwordBody: tpl.body || "",
              });
            }}
          />
          <TextInput
            label="Password email subject"
            value={passwordSubject || ""}
            disabled={readOnly}
            onChange={(event) =>
              onChange({ passwordSubject: event.currentTarget.value })
            }
          />
          <TextInput
            label="Password email body"
            description="Use {{pdfPassword}}. It is removed from the document email."
            value={passwordBody || ""}
            disabled={readOnly}
            onChange={(event) =>
              onChange({ passwordBody: event.currentTarget.value })
            }
          />
        </Stack>
      ) : null}
    </>
  );

  if (grouped) {
    return (
      <Stack spacing="md">
        <Paper withBorder p="md" radius="sm">
          <Text size="xs" weight={600} color="dimmed" mb="sm" transform="uppercase">{i18n.t("Template")}</Text>
          <Stack spacing="sm">
            {templateFields}
            {saveFields}
          </Stack>
        </Paper>
        {between}
        <Paper withBorder p="md" radius="sm">
          <Text size="xs" weight={600} color="dimmed" mb="sm" transform="uppercase">{i18n.t("Attachments")}</Text>
          <Stack spacing="sm">{deliveryFields}</Stack>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack spacing="sm">
      {templateFields}
      {deliveryFields}
      {saveFields}
    </Stack>
  );
}

export default EntityDocumentEmailFields;
